import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from 'react';
import * as api from '../services/api';
import { useAuth } from './AuthContext';

export interface Tenant {
  id: string;
  name: string;
  slug: string;
  domain: string;
  plan: 'starter' | 'professional' | 'enterprise';
  status: 'active' | 'inactive' | 'trial' | 'suspended';
  createdAt: string;
  adminEmail: string;
  adminName: string;
  maxUsers: number;
  currentUsers: number;
  maxOperators: number;
  currentOperators: number;
  features: string[];
  trialEndsAt?: string;
  lastActivityAt: string;
  whiteLabelConfig?: WhiteLabelConfig;
}

export interface WhiteLabelConfig {
  customName: string;
  tagline?: string;
  logoUrl?: string;
  logoSmallUrl?: string;
  faviconUrl?: string;
  loginBgUrl?: string;
  primaryColor: string;
  secondaryColor: string;
  accentColor: string;
  customDomain?: string;
  customDomainVerified: boolean;
  sslStatus: 'pending' | 'active' | 'error' | 'none';
  hideSimulaDeskBranding: boolean;
  customFooterText?: string;
  showPoweredBy: boolean;
  emailFromName?: string;
  emailReplyTo?: string;
  emailHeaderLogoUrl?: string;
  emailFooterHtml?: string;
  customCss?: string;
  isActive: boolean;
  lastUpdatedAt: string;
  lastUpdatedBy: string;
}

export interface AdminUser {
  id: string;
  name: string;
  email: string;
  role: 'admin' | 'qualidade';
  tenantName: string;
  tenantId: string;
  sectorId?: string;
  status: 'active' | 'inactive';
  createdAt: string;
  lastLogin: string;
  tempPassword?: string;
  inviteSent: boolean;
  inviteSentAt?: string;
}

export interface ActivityLog {
  id: string;
  action: 'tenant_created' | 'tenant_updated' | 'tenant_deleted' | 'tenant_status_changed'
    | 'admin_created' | 'admin_updated' | 'admin_deleted' | 'admin_status_changed'
    | 'admin_invite_sent' | 'admin_password_reset';
  entityType: 'tenant' | 'admin';
  entityId: string;
  entityName: string;
  details: string;
  performedBy: string;
  performedAt: string;
}

export const PLAN_CONFIG = {
  starter: { label: 'Starter', color: 'var(--chart-4)', maxUsers: 25, maxOperators: 15 },
  professional: { label: 'Professional', color: 'var(--primary)', maxUsers: 100, maxOperators: 60 },
  enterprise: { label: 'Enterprise', color: 'var(--secondary)', maxUsers: 500, maxOperators: 300 },
} as const;

export const STATUS_CONFIG = {
  active: { label: 'Ativo', color: 'var(--success)', bg: 'color-mix(in srgb, var(--success) 10%, transparent)' },
  inactive: { label: 'Inativo', color: 'var(--muted-foreground)', bg: 'var(--muted)' },
  trial: { label: 'Trial', color: 'var(--warning)', bg: 'color-mix(in srgb, var(--warning) 10%, transparent)' },
  suspended: { label: 'Suspenso', color: 'var(--error)', bg: 'var(--error-muted)' },
} as const;

export const ALL_FEATURES = [
  'auditorias', 'treinamento_ia', 'ligas', 'certificados', 'feedback_360',
  'analytics_avancado', 'api_integracao', 'sso', 'suporte_prioritario', 'white_label',
] as const;

export const FEATURE_LABELS: Record<string, string> = {
  auditorias: 'Auditorias',
  treinamento_ia: 'Treinamento IA',
  ligas: 'Ligas & Gamificacao',
  certificados: 'Certificados',
  feedback_360: 'Feedback 360',
  analytics_avancado: 'Analytics Avancado',
  api_integracao: 'API & Integracao',
  sso: 'SSO / SAML',
  suporte_prioritario: 'Suporte Prioritario',
  white_label: 'White Label',
};

export function generateTempPassword(): string {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
  const specials = '!@#$%';
  let pwd = '';
  for (let i = 0; i < 10; i += 1) pwd += chars[Math.floor(Math.random() * chars.length)];
  pwd += specials[Math.floor(Math.random() * specials.length)];
  return pwd;
}

interface TenantContextType {
  tenants: Tenant[];
  adminUsers: AdminUser[];
  activityLogs: ActivityLog[];
  createTenant: (data: {
    name: string; slug: string; adminEmail: string; adminName: string;
    plan: Tenant['plan']; features: string[];
    tempPassword?: string; sendInvite?: boolean;
  }) => Promise<Tenant>;
  updateTenant: (id: string, data: Partial<Tenant>) => Promise<void>;
  deleteTenant: (id: string) => Promise<void>;
  toggleTenantStatus: (id: string) => void;
  createAdmin: (data: {
    name: string; email: string; role: AdminUser['role']; tenantId: string;
    tenantName?: string; sectorId?: string; tempPassword?: string; sendInvite?: boolean;
  }) => Promise<AdminUser>;
  updateAdmin: (id: string, data: Partial<AdminUser>) => Promise<void>;
  deleteAdmin: (id: string) => Promise<boolean>;
  toggleAdminStatus: (id: string) => void;
  resendInvite: (id: string) => void;
  getTenantById: (id: string) => Tenant | undefined;
  getAdminsByTenant: (tenantId: string) => AdminUser[];
  getAdminCountByTenant: (tenantId: string) => number;
  getTenantOptions: () => { id: string; name: string }[];
  addLog: (log: Omit<ActivityLog, 'id' | 'performedAt'>) => void;
  refreshAdminUsers: () => Promise<void>;
}

const TenantContext = createContext<TenantContextType | undefined>(undefined);

const now = () => new Date().toISOString();
const dateOnly = () => now().split('T')[0];
const makeId = (prefix: string) => {
  const uid = typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  return `${prefix}-${uid}`;
};

function mapWL(row: any): WhiteLabelConfig {
  return {
    customName: row.customName || row.custom_name || '',
    tagline: row.tagline || undefined,
    logoUrl: row.logoUrl || row.logo_url,
    logoSmallUrl: row.logoSmallUrl || row.logo_small_url,
    faviconUrl: row.faviconUrl || row.favicon_url,
    loginBgUrl: row.loginBgUrl || row.login_bg_url,
    primaryColor: row.primaryColor || row.primary_color || '#1D4ED8',
    secondaryColor: row.secondaryColor || row.secondary_color || '#14B8A6',
    accentColor: row.accentColor || row.accent_color || '#F59E0B',
    customDomain: row.customDomain || row.custom_domain,
    customDomainVerified: row.customDomainVerified ?? row.custom_domain_verified ?? false,
    sslStatus: row.sslStatus || row.ssl_status || 'none',
    hideSimulaDeskBranding: row.hideSimulaDeskBranding ?? row.hide_simuladesk_branding ?? false,
    customFooterText: row.customFooterText || row.custom_footer_text,
    showPoweredBy: row.showPoweredBy ?? row.show_powered_by ?? true,
    emailFromName: row.emailFromName || row.email_from_name,
    emailReplyTo: row.emailReplyTo || row.email_reply_to,
    emailHeaderLogoUrl: row.emailHeaderLogoUrl || row.email_header_logo_url,
    emailFooterHtml: row.emailFooterHtml || row.email_footer_html,
    customCss: row.customCss || row.custom_css,
    isActive: row.isActive ?? row.is_active ?? false,
    lastUpdatedAt: row.lastUpdatedAt || row.last_updated_at || now(),
    lastUpdatedBy: row.lastUpdatedBy || row.last_updated_by || 'Sistema',
  };
}

function mapTenant(row: any, wl?: any): Tenant {
  return {
    id: row.id,
    name: row.name,
    slug: row.slug,
    domain: row.domain || '',
    plan: row.plan,
    status: row.status,
    createdAt: row.createdAt || row.created_at || now(),
    adminEmail: row.adminEmail || row.admin_email || '',
    adminName: row.adminName || row.admin_name || '',
    maxUsers: row.maxUsers || row.max_users || 0,
    currentUsers: row.currentUsers || row.current_users || 0,
    maxOperators: row.maxOperators || row.max_operators || 0,
    currentOperators: row.currentOperators || row.current_operators || 0,
    features: row.features || [],
    trialEndsAt: row.trialEndsAt || row.trial_ends_at,
    lastActivityAt: row.lastActivityAt || row.last_activity_at || now(),
    whiteLabelConfig: wl ? mapWL(wl) : undefined,
  };
}

function mapAdmins(usersRows: any[], tenantList: Tenant[], monitorRows: any[]): AdminUser[] {
  const tenantNameById = new Map(tenantList.map((t) => [t.id, t.name]));
  const monitorIds = new Set(
    (monitorRows || []).map((m: any) => m.id).filter(Boolean),
  );
  return (usersRows || [])
    .filter((u) => {
      if (u.role !== 'admin' && u.role !== 'qualidade') return false;
      if (u.monitorId || u.monitor_id) return false;
      if (monitorIds.has(u.id)) return false;
      return true;
    })
    .map((u) => ({
      id: u.id,
      name: u.name,
      email: u.email,
      role: u.role,
      tenantName: tenantNameById.get(u.tenantId || u.tenant_id) || 'N/A',
      tenantId: u.tenantId || u.tenant_id,
      sectorId: u.sectorId || u.sector_id || undefined,
      status: (u.isActive || u.is_active ? 'active' : 'inactive') as 'active' | 'inactive',
      createdAt: u.createdAt || u.created_at || now(),
      lastLogin: u.lastLogin || u.last_login || '-',
      inviteSent: true,
      inviteSentAt: u.createdAt || u.created_at,
    }))
    .sort((a, b) => {
      const roleOrder = { qualidade: 0, admin: 1 } as const;
      const roleDiff = (roleOrder[a.role as keyof typeof roleOrder] ?? 2) - (roleOrder[b.role as keyof typeof roleOrder] ?? 2);
      if (roleDiff !== 0) return roleDiff;
      return a.name.localeCompare(b.name);
    });
}

export function TenantProvider({ children }: { children: ReactNode }) {
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [adminUsers, setAdminUsers] = useState<AdminUser[]>([]);
  const [activityLogs, setActivityLogs] = useState<ActivityLog[]>([]);

  const { user, isAuthLoading } = useAuth();

  useEffect(() => {
    if (isAuthLoading) return;
    if (!user) {
      setTenants([]);
      setAdminUsers([]);
      setActivityLogs([]);
      return;
    }

    const load = async () => {
      try {
        const [tenantRows, usersRows, logsRows, wlRows, monitorRows] = await Promise.all([
          api.listEntities<any>('tenants'),
          api.listEntities<any>('users'),
          api.listEntities<any>('tenant-activity-logs'),
          api.listEntities<any>('white-label-configs'),
          api.listEntities<any>('monitors'),
        ]);

        const wlByTenant = new Map((wlRows || []).map((w) => [w.tenantId || w.tenant_id, w]));
        const tenantList = (tenantRows || []).map((row) => mapTenant(row, wlByTenant.get(row.id)));
        setTenants(tenantList);

        setAdminUsers(mapAdmins(usersRows || [], tenantList, monitorRows || []));

        setActivityLogs(
          (logsRows || []).map((l) => ({
            id: l.id,
            action: l.action,
            entityType: l.entityType || l.entity_type,
            entityId: l.entityId || l.entity_id,
            entityName: l.entityName || l.entity_name,
            details: l.details || '',
            performedBy: l.performedBy || l.performed_by || 'Sistema',
            performedAt: l.performedAt || l.performed_at || now(),
          })),
        );
      } catch (err) {
        console.error('[TenantContext] Failed to load backend data:', err);
      }
    };
    load();
  }, [user?.id, isAuthLoading]);

  const refreshAdminUsers = useCallback(async () => {
    if (!user) return;
    try {
      const [usersRows, monitorRows] = await Promise.all([
        api.listEntities<any>('users'),
        api.listEntities<any>('monitors'),
      ]);
      setAdminUsers(mapAdmins(usersRows || [], tenants, monitorRows || []));
    } catch (err) {
      console.error('[TenantContext] Failed to refresh admin users:', err);
    }
  }, [user, tenants]);

  const addLog = useCallback((log: Omit<ActivityLog, 'id' | 'performedAt'>) => {
    const entry: ActivityLog = { ...log, id: makeId('log'), performedAt: now() };
    setActivityLogs((prev) => [entry, ...prev]);
    api.createEntity('tenant-activity-logs', {
      id: entry.id,
      tenantId: tenants.find((t) => t.id === entry.entityId)?.id || null,
      action: entry.action,
      entityType: entry.entityType,
      entityId: entry.entityId,
      entityName: entry.entityName,
      details: entry.details,
      performedBy: entry.performedBy,
      performedAt: entry.performedAt,
    } as any).catch(console.error);
  }, [tenants]);

  const createAdmin = useCallback(async (data: {
    name: string; email: string; role: AdminUser['role']; tenantId: string;
    tenantName?: string; sectorId?: string; tempPassword?: string; sendInvite?: boolean;
  }): Promise<AdminUser> => {
    const admin: AdminUser = {
      id: makeId('user'),
      name: data.name,
      email: data.email,
        role: data.role,
        tenantName: data.tenantName || tenants.find((t) => t.id === data.tenantId)?.name || 'N/A',
        tenantId: data.tenantId,
        sectorId: data.sectorId,
        status: 'active',
      createdAt: now(),
      lastLogin: '-',
      tempPassword: data.tempPassword || generateTempPassword(),
      inviteSent: data.sendInvite ?? false,
      inviteSentAt: data.sendInvite ? now() : undefined,
    };
    setAdminUsers((prev) => [...prev, admin]);
      await api.createEntity('users', {
        id: admin.id,
        name: admin.name,
        email: admin.email,
        role: admin.role,
        tenantId: admin.tenantId,
        sectorId: admin.sectorId,
        isActive: true,
      } as any);
    return admin;
  }, [tenants]);

  const createTenant = useCallback(async (data: {
    name: string; slug: string; adminEmail: string; adminName: string;
    plan: Tenant['plan']; features: string[];
    tempPassword?: string; sendInvite?: boolean;
  }): Promise<Tenant> => {
    const cfg = PLAN_CONFIG[data.plan];
    const tenant: Tenant = {
      id: makeId('tenant'),
      name: data.name,
      slug: data.slug,
      domain: `${data.slug}.simuladesk.com`,
      plan: data.plan,
      status: 'trial',
      createdAt: now(),
      adminEmail: data.adminEmail,
      adminName: data.adminName,
      maxUsers: cfg.maxUsers,
      currentUsers: 1,
      maxOperators: cfg.maxOperators,
      currentOperators: 0,
      features: data.features,
      trialEndsAt: new Date(Date.now() + 30 * 86400000).toISOString(),
      lastActivityAt: now(),
    };
    setTenants((prev) => [...prev, tenant]);
    await api.createEntity('tenants', tenant as any);

    const admin = await createAdmin({
      name: data.adminName,
      email: data.adminEmail,
      role: 'admin',
      tenantId: tenant.id,
      tenantName: tenant.name,
      tempPassword: data.tempPassword,
      sendInvite: data.sendInvite,
    });

    addLog({
      action: 'tenant_created',
      entityType: 'tenant',
      entityId: tenant.id,
      entityName: tenant.name,
      details: `Empresa criada com plano ${PLAN_CONFIG[data.plan].label}`,
      performedBy: 'Super Admin',
    });
    addLog({
      action: 'admin_created',
      entityType: 'admin',
      entityId: admin.id,
      entityName: admin.name,
      details: `Admin criado para ${tenant.name}`,
      performedBy: 'Super Admin',
    });
    return tenant;
  }, [addLog, createAdmin]);

  const updateTenant = useCallback(async (id: string, data: Partial<Tenant>) => {
    setTenants((prev) => prev.map((t) => (t.id === id ? { ...t, ...data } : t)));
    await api.saveEntity('tenants', { id, ...data } as any);
    addLog({
      action: 'tenant_updated',
      entityType: 'tenant',
      entityId: id,
      entityName: data.name || tenants.find((t) => t.id === id)?.name || id,
      details: `Empresa atualizada: ${Object.keys(data).join(', ')}`,
      performedBy: 'Super Admin',
    });
  }, [addLog, tenants]);

  const deleteTenant = useCallback(async (id: string) => {
    const tenant = tenants.find((t) => t.id === id);
    setTenants((prev) => prev.filter((t) => t.id !== id));
    await api.deleteEntity('tenants', id);
    addLog({
      action: 'tenant_deleted',
      entityType: 'tenant',
      entityId: id,
      entityName: tenant?.name || id,
      details: 'Tenant removido',
      performedBy: 'Super Admin',
    });
  }, [addLog, tenants]);

  const toggleTenantStatus = useCallback((id: string) => {
    const tenant = tenants.find((t) => t.id === id);
    if (!tenant) return;
    const nextStatus = tenant.status === 'active' ? 'inactive' : 'active';
    updateTenant(id, { status: nextStatus });
    addLog({
      action: 'tenant_status_changed',
      entityType: 'tenant',
      entityId: id,
      entityName: tenant.name,
      details: `Status alterado para ${nextStatus}`,
      performedBy: 'Super Admin',
    });
  }, [tenants, updateTenant, addLog]);

  const updateAdmin = useCallback(async (id: string, data: Partial<AdminUser>) => {
    setAdminUsers((prev) => prev.map((a) => (a.id === id ? { ...a, ...data } : a)));
    await api.saveEntity('users', {
      id,
      name: data.name,
      email: data.email,
      role: data.role,
      tenantId: data.tenantId,
      isActive: data.status ? data.status === 'active' : undefined,
    } as any);
  }, []);

  const deleteAdmin = useCallback(async (id: string) => {
    const success = await api.deleteEntity('users', id);
    if (success) {
      setAdminUsers((prev) => prev.filter((a) => a.id !== id));
    }
    return success;
  }, []);

  const toggleAdminStatus = useCallback((id: string) => {
    const admin = adminUsers.find((a) => a.id === id);
    if (!admin) return;
    const nextStatus = admin.status === 'active' ? 'inactive' : 'active';
    updateAdmin(id, { status: nextStatus });
    addLog({
      action: 'admin_status_changed',
      entityType: 'admin',
      entityId: id,
      entityName: admin.name,
      details: `Status alterado para ${nextStatus}`,
      performedBy: 'Super Admin',
    });
  }, [adminUsers, updateAdmin, addLog]);

  const resendInvite = useCallback((id: string) => {
    setAdminUsers((prev) => prev.map((a) => (a.id === id ? { ...a, inviteSent: true, inviteSentAt: now() } : a)));
    addLog({
      action: 'admin_invite_sent',
      entityType: 'admin',
      entityId: id,
      entityName: adminUsers.find((a) => a.id === id)?.name || id,
      details: 'Convite reenviado',
      performedBy: 'Super Admin',
    });
  }, [adminUsers, addLog]);

  const getTenantById = useCallback((id: string) => tenants.find((t) => t.id === id), [tenants]);
  const getAdminsByTenant = useCallback((tenantId: string) => adminUsers.filter((a) => a.tenantId === tenantId), [adminUsers]);
  const getAdminCountByTenant = useCallback((tenantId: string) => adminUsers.filter((a) => a.tenantId === tenantId).length, [adminUsers]);
  const getTenantOptions = useCallback(() => tenants.map((t) => ({ id: t.id, name: t.name })), [tenants]);

  return (
    <TenantContext.Provider
      value={{
        tenants,
        adminUsers,
        activityLogs,
        createTenant,
        updateTenant,
        deleteTenant,
        toggleTenantStatus,
        createAdmin,
        updateAdmin,
        deleteAdmin,
        toggleAdminStatus,
        resendInvite,
        getTenantById,
        getAdminsByTenant,
        getAdminCountByTenant,
        getTenantOptions,
        addLog,
        refreshAdminUsers,
      }}
    >
      {children}
    </TenantContext.Provider>
  );
}

export function useTenantContext() {
  const ctx = useContext(TenantContext);
  if (!ctx) throw new Error('useTenantContext must be used within TenantProvider');
  return ctx;
}
